import {
  BarChart3,
  BookOpenCheck,
  BriefcaseBusiness,
  Building2,
  BusFront,
  CalendarCheck2,
  CheckCircle2,
  ClipboardCheck,
  FileBarChart,
  FileCheck2,
  FileSpreadsheet,
  Files,
  GraduationCap,
  Landmark,
  LayoutDashboard,
  LineChart,
  ListChecks,
  MapPinned,
  Megaphone,
  MessageCircle,
  ReceiptIndianRupee,
  Search,
  ShieldAlert,
  Sparkles,
  UserCheck,
  UserPlus,
  Users,
  WalletCards,
  Workflow,
} from "lucide-react";
import { CTA_SECONDARY_LABEL, DEMO_BOOKING_ROUTE } from "../../components/marketing/navigation";

// PERF-9: w480/w768 exist so a phone lands on a rung that matches its real CSS width
// instead of over-fetching w960. Mirrors RESPONSIVE_WIDTHS in
// frontend/scripts/generate_product_story_ui_assets.py — the generator is the source of
// truth and `npm run prebuild` fails if the two drift.
const RESPONSIVE_STORY_WIDTHS = Object.freeze([480, 640, 768, 960, 1280]);
const RESPONSIVE_CROP_WIDTHS = Object.freeze([320, 480, 640, 960]);

const stemOf = (image) => String(image).replace(/\.webp$/, "");

// Rung widths for one asset, ALWAYS including its native width. Mirrors `delivery_ladder` in
// the generator. The native width must be a real rung: these frames render up to ~738 CSS px, so
// a DPR-2 viewport asks for ~1476px — above every fixed step — and would otherwise fall back to
// the lossless master. `scene.image` (the master) is therefore the <img src> only, never a rung.
const ladderFor = (nativeWidth, steps) => [
  ...steps.filter((width) => width < nativeWidth),
  nativeWidth,
];

const srcSetFor = (image, nativeWidth, steps, extension) => {
  const stem = stemOf(image);
  return ladderFor(nativeWidth, steps)
    .map((width) => `${stem}.w${width}.${extension} ${width}w`)
    .join(", ");
};

export const productStorySrcSetFor = (image, nativeWidth) =>
  srcSetFor(image, nativeWidth, RESPONSIVE_STORY_WIDTHS, "webp");

export const productStoryAvifSrcSetFor = (image, nativeWidth) =>
  srcSetFor(image, nativeWidth, RESPONSIVE_STORY_WIDTHS, "avif");

// Wraps a preloader <img> in a detached <picture> that advertises the AVIF rung, so the browser's
// resource-selection algorithm picks the SAME candidate it will pick for the visible element.
// The <img> keeps a parent reference to the <picture>, so storing the image alone keeps both alive.
export function attachAvifSource(image, avifSrcSet, sizes) {
  if (typeof document === "undefined" || !document.createElement || !avifSrcSet) return image;
  try {
    const picture = document.createElement("picture");
    const source = document.createElement("source");
    source.type = "image/avif";
    source.sizes = sizes;
    source.srcset = avifSrcSet;
    picture.appendChild(source);
    picture.appendChild(image);
  } catch {
    // Warming is opportunistic — a host without a real DOM keeps the plain <img> path.
  }
  return image;
}

export const productStoryCropFor = (scene, fragment) => {
  const { crop } = fragment;
  const left = Math.round(scene.imageWidth * crop.x / 100);
  const top = Math.round(scene.imageHeight * crop.y / 100);
  const right = Math.round(scene.imageWidth * (crop.x + crop.w) / 100);
  const bottom = Math.round(scene.imageHeight * (crop.y + crop.h) / 100);
  const image = `${stemOf(scene.image)}.${fragment.id}.crop.webp`;
  const width = right - left;
  return Object.freeze({
    image,
    width,
    height: bottom - top,
    srcSet: srcSetFor(image, width, RESPONSIVE_CROP_WIDTHS, "webp"),
    avifSrcSet: srcSetFor(image, width, RESPONSIVE_CROP_WIDTHS, "avif"),
  });
};

const media = (filename, width, height) => Object.freeze({
  image: `/marketing/product-story/v2/${filename}`,
  imageSrcSet: productStorySrcSetFor(`/marketing/product-story/v2/${filename}`, width),
  imageAvifSrcSet: productStoryAvifSrcSetFor(`/marketing/product-story/v2/${filename}`, width),
  imageWidth: width,
  imageHeight: height,
});

// The homepage enquiry block is the canonical booking journey.  Keeping this a
// real route (instead of a modules-page modal action) makes every scene CTA
// shareable, middle-clickable and predictable across desktop and mobile.
export const PRODUCT_STORY_DEMO_ROUTE = DEMO_BOOKING_ROUTE;
const action = (label = CTA_SECONDARY_LABEL) => Object.freeze({ label, to: DEMO_BOOKING_ROUTE, action: "demo" });
const route = (label, to) => Object.freeze({ label, to });
const card = (icon, title, description) => Object.freeze({ icon, title, description });
const fragment = (featureIndex, id, label, crop, orbitSlot) => Object.freeze({
  featureIndex,
  id,
  label,
  crop: Object.freeze(crop),
  orbitSlot,
});

export const PRODUCT_STORY_SCENES = Object.freeze([
  Object.freeze({
    id: "unified-erp",
    eyebrow: "Unified School ERP",
    title: "One Platform.",
    highlightedTitle: "Every School Need.",
    description:
      "Vidyapeeth360 connects admissions, academics, fees, people, transport, tasks and family communication across one permission-aware K–12 platform. Start with the included core and add plan- or rollout-controlled capabilities as your operating needs grow.",
    problem: "Separate tools make teams re-enter the same school context and reconcile competing versions of a record.",
    benefits: Object.freeze([
      "Connected records across school workflows",
      "Current, role-aware views for authorised teams",
      "Plan, provider and rollout requirements shown before enablement",
    ]),
    featureCards: Object.freeze([
      card(GraduationCap, "Academics", "Curriculum, timetable, assignments, exams and results"),
      card(ReceiptIndianRupee, "Fee & Finance", "Invoice-led billing, collections, expenses and reports"),
      card(Users, "People & Payroll", "Staff, leave and payroll where the HR workspace is enabled"),
      card(Building2, "Administration", "Roles, tasks, transport and operating controls"),
    ]),
    visualFragments: Object.freeze([
      fragment(0, "daily-workflow", "Connected daily workflow", { x: 12.1, y: 17.4, w: 68.9, h: 10.5 }, "top-left"),
      fragment(3, "assigned-work", "Human-assigned work queue", { x: 82, y: 7.3, w: 17.7, h: 42 }, "right"),
      fragment(1, "task-handoffs", "Cross-team task hand-offs", { x: 12.3, y: 82.2, w: 68.6, h: 14.1 }, "bottom-left"),
    ]),
    ...media("01-unified-erp.webp", 1586, 992),
    imageAlt: "Conceptual executive dashboard connecting academics, finance, staff and administration.",
    primaryCTA: action(),
    secondaryCTA: route("Explore modules", "/modules"),
    accent: "--vp-accent-active",
    availability: "Core platform with plan- and rollout-controlled extensions",
  }),
  Object.freeze({
    id: "academics",
    eyebrow: "Academics",
    // POS-7: concrete mechanism, not transformation rhetoric.
    title: "Curriculum to Report Card.",
    highlightedTitle: "One Academic Record.",
    description:
      "Plan curriculum and timetables, publish assignments, mark attendance, schedule examinations and issue board-configured report cards from connected academic records.",
    problem: "Curriculum, timetable, attendance, assignments and results lose meaning when each is maintained in isolation.",
    benefits: Object.freeze([
      "Build class, section, subject and curriculum structures",
      "Record daily or period attendance and assignment submissions",
      "Schedule examinations and publish approved report cards",
    ]),
    featureCards: Object.freeze([
      card(BookOpenCheck, "Curriculum Planning", "Track adopted syllabus plans and review progress"),
      card(CalendarCheck2, "Timetable Management", "Coordinate classes, subjects, rooms and teachers"),
      card(UserCheck, "Student Attendance", "Record daily or period attendance with corrections"),
      card(FileCheck2, "Result Publishing", "Release approved, board-configured report cards"),
    ]),
    visualFragments: Object.freeze([
      fragment(0, "curriculum-progress", "Curriculum progress", { x: 61.6, y: 11.5, w: 35.5, h: 43 }, "top-right"),
      fragment(2, "attendance-register", "Attendance register", { x: 12.8, y: 56.6, w: 19.6, h: 27.8 }, "bottom-left"),
      fragment(3, "report-card-approval", "Report-card approvals", { x: 74.5, y: 56.5, w: 22.8, h: 27.8 }, "bottom-right"),
    ]),
    ...media("02-academics.webp", 1536, 1024),
    imageAlt: "Conceptual academic dashboard with timetable, attendance, assignments and progress.",
    primaryCTA: action(),
    secondaryCTA: route("Explore academics", "/modules?group=academics"),
    accent: "--vp-accent-active",
    availability: "Core academic workflows with selected plan-gated extensions",
  }),
  Object.freeze({
    id: "fee-finance",
    eyebrow: "Fee & Finance",
    title: "Invoice to Receipt.",
    highlightedTitle: "One Ledger.",
    description:
      "Create fee structures and invoices, record counter evidence, take online payments through a configured school-owned Razorpay or Cashfree account, track dues, govern concessions and scholarships, and report from the same ledger. Expense, accounting and payroll capabilities remain plan- or rollout-controlled.",
    problem: "Disconnected invoices, payment evidence, concessions and expense records leave finance teams reconciling the same transaction more than once.",
    benefits: Object.freeze([
      "Automate opt-in demand generation and supported reminders",
      "Keep concessions, receipts, refunds and expenses reviewable",
      "Review current billed, collected, due and overdue totals",
    ]),
    featureCards: Object.freeze([
      card(WalletCards, "Fee Collection", "Invoices, evidence, receipts and configured online payments"),
      card(BriefcaseBusiness, "Expense Management", "Requests, approvals, categories and ledger evidence"),
      card(GraduationCap, "Discount & Scholarship", "Governed requests and authorised decisions"),
      card(FileBarChart, "Accounting Reports", "Plan-gated finance views and exports"),
    ]),
    visualFragments: Object.freeze([
      fragment(0, "provider-evidence", "Payment provider evidence", { x: 12.6, y: 25.4, w: 44.9, h: 25.8 }, "top-left"),
      fragment(0, "overdue-review", "Overdue review queue", { x: 58.1, y: 25.4, w: 40.6, h: 25.8 }, "top-right"),
      fragment(3, "reconciliation-boundary", "Clear reconciliation boundary", { x: 12.6, y: 75.8, w: 44.8, h: 20.7 }, "bottom-left"),
    ]),
    ...media("03-fee-finance.webp", 1568, 1003),
    imageAlt: "Conceptual fee and finance dashboard with collections, dues, expenses and reports.",
    primaryCTA: action(),
    secondaryCTA: route("Explore finance", "/modules?group=fees"),
    accent: "--vp-orange-text",
    availability: "Provider, plan and approval requirements vary by workflow",
  }),
  Object.freeze({
    id: "hr-payroll",
    eyebrow: "HR & Payroll",
    // POS-7: concrete mechanism, not empowerment rhetoric.
    title: "Staff Records to Payroll.",
    highlightedTitle: "One Approval Trail.",
    description:
      "Where the HR & Payroll workspace is enabled, manage employee records, recruitment, attendance evidence, leave, performance workflows, payroll and self-service from permission-scoped people operations.",
    problem: "Staff records, leave decisions, attendance evidence and payroll inputs drift when ownership moves between files and teams.",
    benefits: Object.freeze([
      "Maintain employee, role and statutory records",
      "Route leave, payroll and sensitive changes through approvals",
      "Give staff scoped self-service for supported records",
    ]),
    featureCards: Object.freeze([
      card(Users, "Staff Management", "Employee records, roles and documents"),
      card(UserCheck, "Staff Attendance", "Workforce attendance evidence and corrections"),
      card(CalendarCheck2, "Leave Management", "Balances, requests and approval queues"),
      card(ReceiptIndianRupee, "Payroll", "Payroll and employee documents — still rolling out"),
    ]),
    visualFragments: Object.freeze([
      fragment(0, "staff-directory", "Permission-scoped staff directory", { x: 8, y: 10, w: 50, h: 28.5 }, "left"),
      fragment(2, "leave-review", "Leave review", { x: 59.6, y: 41.5, w: 38.9, h: 22 }, "top-right"),
      fragment(3, "payroll-maker-checker", "Second-person payroll approval", { x: 59.6, y: 66.2, w: 38.9, h: 31.4 }, "bottom-right"),
    ]),
    ...media("04-hr-payroll.webp", 1586, 992),
    imageAlt: "Conceptual HR dashboard with staff attendance, leave and payroll workflows.",
    primaryCTA: action(),
    secondaryCTA: route("Explore staff management", "/modules/staff-management"),
    accent: "--vp-ai-ink",
    availability: "Still rolling out — enabled school by school",
  }),
  Object.freeze({
    id: "admissions",
    eyebrow: "Admissions",
    // POS-7: en-IN spelling + the registry-safe outcome phrasing (matches the hero tagline).
    title: "From Enquiry to",
    highlightedTitle: "Enrolment. One Record.",
    description:
      "Capture enquiries from supported public forms, manage a six-stage admissions pipeline, collect applications and documents, review decisions, then convert an admitted lead into a student record through a controlled workflow.",
    problem: "Enquiries are easy to lose when follow-up, documents, decisions and student creation happen in separate places.",
    benefits: Object.freeze([
      "Capture enquiries and flag recent duplicates",
      "Keep ownership, follow-up and stage history visible",
      "Convert admitted leads with carried-forward documents",
    ]),
    featureCards: Object.freeze([
      card(Search, "Enquiry", "Public capture, duplicate checks and ownership"),
      card(Files, "Application", "Structured forms and document collection"),
      card(CheckCircle2, "Stage Review", "Staff-owned decisions and stage history"),
      card(UserPlus, "Student Conversion", "Controlled lead-to-student handoff"),
    ]),
    visualFragments: Object.freeze([
      fragment(0, "admissions-pipeline", "Six-stage admissions pipeline", { x: 7.6, y: 24.8, w: 64.3, h: 42 }, "left"),
      fragment(1, "document-verification", "Document verification", { x: 73, y: 22.1, w: 25.7, h: 40.2 }, "top-right"),
      fragment(2, "conversion-confirmation", "Authorised student conversion", { x: 73, y: 63.3, w: 25.7, h: 20.4 }, "bottom-right"),
    ]),
    ...media("05-admissions.webp", 1586, 992),
    imageAlt: "Conceptual admissions dashboard showing enquiry-to-enrolment stages.",
    primaryCTA: action(),
    secondaryCTA: route("Explore admissions", "/modules/enquiry-management"),
    accent: "--vp-success",
    availability: "Core CRM workflow; public form and OTP setup may be required",
  }),
  Object.freeze({
    id: "aira-ai",
    eyebrow: "Aira AI",
    title: "Turn School Data Into",
    highlightedTitle: "Better Decisions.",
    description:
      "Aira answers supported, permission-scoped questions from live school records, drafts reviewable summaries and proposes supported actions for a person to confirm. Deterministic risk signals and governed reports keep decisions explainable.",
    problem: "School leaders need answers across operating records without giving an assistant unrestricted access or autonomous control.",
    benefits: Object.freeze([
      "Permission-checked answers from supported records",
      "Drafted summaries and reports remain reviewable",
      "Supported actions wait for explicit human confirmation",
    ]),
    featureCards: Object.freeze([
      card(Sparkles, "Aira Assistant", "Text or configured voice over supported school records"),
      card(BarChart3, "Talk to Your Data", "Answers routed through a vetted report catalogue"),
      card(FileCheck2, "Report Card AI", "Teacher-reviewed drafts from permitted inputs"),
      card(Workflow, "Governed Automations", "A separate plan feature; starter rules begin disabled"),
    ]),
    visualFragments: Object.freeze([
      fragment(0, "permission-boundary", "Permission boundary", { x: 7.2, y: 6.2, w: 49, h: 11.4 }, "top-left"),
      fragment(1, "grounded-sources", "Source-backed answer", { x: 12.8, y: 31, w: 35.3, h: 41.5 }, "bottom-left"),
      fragment(3, "confirmation-audit", "Confirmation gate and audit trail", { x: 57.8, y: 52, w: 40.9, h: 44 }, "right"),
    ]),
    ...media("06-aira-ai.webp", 1568, 1003),
    imageAlt: "Conceptual governed Aira workspace connecting insights, reports and reviewed actions.",
    primaryCTA: action(),
    secondaryCTA: route("Explore Aira", "/aira"),
    accent: "--vp-ai-ink",
    availability: "Capabilities depend on your plan, supported workflow and provider setup",
  }),
  Object.freeze({
    id: "parent-student",
    eyebrow: "Parent & Student Experience",
    title: "Keep Every Family",
    highlightedTitle: "Connected.",
    description:
      "Responsive parent and student portals provide scoped access to attendance, homework, timetables, published results, invoices, receipts and notices. Native apps remain in controlled early access and should be confirmed before rollout.",
    problem: "Families lose confidence when updates arrive through unrelated channels without one authenticated view of their own records.",
    benefits: Object.freeze([
      "Current account views and configured notifications",
      "Authenticated access to the family’s own records",
      "Availability stated before rollout",
    ]),
    featureCards: Object.freeze([
      card(BusFront, "Bus Update", "When live tracking and alerts are enabled"),
      card(BookOpenCheck, "Homework Update", "Published work and submission status"),
      card(ReceiptIndianRupee, "Payment Confirmed", "Invoice and receipt status"),
      card(Megaphone, "New Announcement", "Configured school updates"),
    ]),
    visualFragments: Object.freeze([
      fragment(0, "family-attendance", "Attendance at a glance", { x: 6.8, y: 8.4, w: 24.7, h: 30.1 }, "top-left"),
      fragment(1, "homework-status", "Homework status", { x: 32, y: 49, w: 38.7, h: 41.5 }, "bottom-left"),
      fragment(3, "family-notices", "Family notices", { x: 71.8, y: 49, w: 27.2, h: 43.3 }, "right"),
    ]),
    ...media("07-parent-student.webp", 1568, 1003),
    imageAlt: "Illustrative responsive family portal for attendance, homework, fees and school updates.",
    primaryCTA: route("Explore parent and student mobile", "/mobile-app"),
    secondaryCTA: action(),
    accent: "--vp-accent-active",
    availability: "Responsive portals available; native applications in controlled early access",
  }),
  Object.freeze({
    id: "transport-safety",
    eyebrow: "Transport & Safety",
    title: "Visibility and Safety",
    highlightedTitle: "Beyond the Classroom.",
    description:
      "Connect routes, stops, vehicles, crew, rider allocation and transport fees. Where Live Bus Tracking is included and compatible location input is available, run trips with freshness-aware location, append-only boarding events, SOS and parent alerts. A separate visitor module records expected and on-premises guests.",
    problem: "Route plans, trip events, rider context and safety follow-up become difficult to verify when each sits in a different log.",
    benefits: Object.freeze([
      "See live or stale trip status where tracking is enabled",
      "Keep boarding and alighting events append-only and reviewable",
      "Track vehicle, crew and transport exceptions",
    ]),
    featureCards: Object.freeze([
      card(MapPinned, "Live Tracking", "Requires an included plan and compatible location input"),
      card(ListChecks, "Boarding Log", "Append-only rider events for active trips"),
      card(ClipboardCheck, "Visitor Register", "A separate expected and on-premises guest workflow"),
      card(ShieldAlert, "SOS & Alerts", "Trip exceptions and configured parent notifications"),
    ]),
    visualFragments: Object.freeze([
      fragment(0, "route-context", "Provider-compatible live location", { x: 7.5, y: 9.2, w: 63.4, h: 39 }, "top-left"),
      fragment(1, "boarding-log", "Append-only boarding log", { x: 7.5, y: 51, w: 63.4, h: 45 }, "bottom-left"),
      fragment(3, "safety-review", "Safety alert review", { x: 72.5, y: 50.8, w: 26.4, h: 45 }, "bottom-right"),
    ]),
    ...media("08-transport-safety.webp", 1568, 980),
    imageAlt: "Conceptual transport command view with trips, boarding and safety exceptions.",
    primaryCTA: action(),
    secondaryCTA: route("Explore transport", "/modules/transport-management"),
    accent: "--vp-info",
    availability: "Live tracking and alerts require plan, location input and configuration",
  }),
  Object.freeze({
    id: "communication",
    eyebrow: "Communication & Engagement",
    title: "Bring School Communication",
    highlightedTitle: "to Life.",
    description:
      "Publish notices, announcements and circulars to selected audiences over configured in-app, SMS, WhatsApp and email channels, then review the evidence each channel actually returns. Moderated class chat keeps subject rooms scoped to the right sections.",
    problem: "A message is not operational evidence when audience, draft, channel and provider response cannot be reviewed together.",
    benefits: Object.freeze([
      "Target audiences by role, class and section",
      "Keep content review separate from delivery evidence",
      "Review attempted, delivered, read or failed status where supported",
    ]),
    featureCards: Object.freeze([
      card(Megaphone, "Notice Board", "Pinned, categorised updates for selected audiences"),
      card(MessageCircle, "Class Chat", "Moderated, section-scoped subject rooms"),
      card(LayoutDashboard, "Announcements", "Governed audience and channel selection"),
      card(Files, "Circular Management", "Official circulars with delivery evidence"),
    ]),
    visualFragments: Object.freeze([
      fragment(0, "governed-announcement", "Audience-scoped announcement", { x: 13.8, y: 12.6, w: 36.7, h: 44.9 }, "top-left"),
      fragment(1, "moderated-channel", "Moderated section channel", { x: 51.5, y: 12.6, w: 47.3, h: 54.3 }, "top-right"),
      fragment(2, "delivery-evidence", "Channel-specific delivery evidence", { x: 51.5, y: 68.3, w: 47.3, h: 27.4 }, "bottom-right"),
    ]),
    ...media("09-communication.webp", 1586, 992),
    imageAlt: "Conceptual communication workspace for notices, class rooms and broadcasts.",
    primaryCTA: action(),
    secondaryCTA: route("Explore communication", "/modules?group=comms"),
    accent: "--vp-danger",
    availability: "Delivery evidence depends on configured provider and channel support",
  }),
  Object.freeze({
    id: "analytics-reports",
    eyebrow: "Analytics & Reports",
    title: "See What Matters.",
    highlightedTitle: "Act With Confidence.",
    description:
      "Use role-aware dashboards and canonical student, fee, attendance, exam and MIS reports with request-time KPIs, accessible charts and supported CSV or XLSX exports. Custom reports are constrained to allow-listed student and fee roll-ups; branch comparison requires Group & Chain.",
    problem: "Leadership decisions slow down when every team calculates the same indicator from a different extract.",
    benefits: Object.freeze([
      "Review current KPIs from supported source records",
      "Export supported reports with active filters",
      "Explain selected trends and risk signals without hiding their drivers",
    ]),
    featureCards: Object.freeze([
      card(LayoutDashboard, "Dashboard Analytics", "Role-aware operating views"),
      card(FileSpreadsheet, "Custom Reports", "Allow-listed student and fee roll-ups"),
      card(LineChart, "Performance Insights", "Explainable indicators and contributing drivers"),
      card(Landmark, "Branch-wise Reports", "Available with the Group & Chain workspace"),
    ]),
    visualFragments: Object.freeze([
      fragment(0, "cross-workspace-indicators", "Cross-workspace indicators", { x: 14.1, y: 8.4, w: 60, h: 39.3 }, "top-left"),
      fragment(1, "report-catalogue", "Supported report catalogue", { x: 14.1, y: 49.5, w: 60.2, h: 44.7 }, "bottom-left"),
      fragment(3, "role-aware-export", "Role-aware export centre", { x: 75.2, y: 45.5, w: 23.7, h: 22.8 }, "right"),
    ]),
    ...media("10-analytics-reports.webp", 1586, 992),
    imageAlt: "Conceptual leadership dashboard with operational and academic reports.",
    primaryCTA: action(),
    secondaryCTA: route("Explore analytics", "/modules?group=reports"),
    accent: "--vp-info",
    availability: "Report scope, exports and branch comparison depend on your plan",
  }),
]);

export const PRODUCT_STORY_SCENE_IDS = Object.freeze(PRODUCT_STORY_SCENES.map((scene) => scene.id));
